/**
 * Interaction catalog — QuestionTypeEnum product identity for Hub AI dropdown.
 *
 * EMG-GQ-ENUM-CATALOG: slug/code = enum value; label from shared
 * ``questionTypeLabels`` (bilingual) — not raw Agent slugs.
 * Option field bounds mirror Agent `question_type_config.py`.
 */

import type { PromptTranslations } from './i18n';
import { getQuestionTypeLabel } from './questionTypeLabels';

export type InteractionEnumSlug =
    | 'scq'
    | 'mcq'
    | 'true_false'
    | 'gap_typing'
    | 'gap_dropdown'
    | 'gap_word_bank'
    | 'gap_drag_words'
    | 'matching_text_pairs'
    | 'matching_zone_drop'
    | 'ordering'
    | 'speak'
    | 'write';

export interface InteractionOptionField {
    key: string;
    /** English fallback label. */
    label: string;
    /** EAA-2031: translated label key — the panel shows ``t[labelKey]``, never raw English. */
    labelKey?: keyof PromptTranslations;
    type: 'number' | 'select';
    defaultValue: string | number;
    min?: number;
    max?: number;
    options?: Array<{ label: string; value: string | number }>;
}

export interface InteractionCatalogEntry {
    slug: InteractionEnumSlug;
    /** Clear label for the AI type dropdown (not a slug). */
    label: string;
    /** Agent product code (= enum). */
    code: InteractionEnumSlug;
    optionFields: InteractionOptionField[];
}

const MEDIA_OPTIONS = [
    { label: 'None', value: 'none' },
    { label: 'Passage', value: 'passage' },
    { label: 'Image', value: 'image' },
    { label: 'Audio', value: 'audio' },
] as const;

/** Shared media field — compose Agent catalog `{enum}_{media}`. */
export const INTERACTION_MEDIA_FIELD: InteractionOptionField = {
    key: 'media',
    label: 'Media', labelKey: 'media',
    type: 'select',
    defaultValue: 'none',
    options: [...MEDIA_OPTIONS],
};

/**
 * Dropdown AI interaction products — order matches Agent INTERACTION_TYPES
 * grouping (choice → gap → matching → other).
 */
export const INTERACTION_CATALOG: InteractionCatalogEntry[] = [
    {
        slug: 'scq',
        label: getQuestionTypeLabel('scq', 'en'),
        code: 'scq',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_questions', label: 'Sub-questions', labelKey: 'numQuestions', type: 'number', defaultValue: 1, min: 1, max: 20 },
            { key: 'num_options', label: 'Options', labelKey: 'numOptions', type: 'number', defaultValue: 4, min: 2, max: 8 },
        ],
    },
    {
        slug: 'mcq',
        label: getQuestionTypeLabel('mcq', 'en'),
        code: 'mcq',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_questions', label: 'Sub-questions', labelKey: 'numQuestions', type: 'number', defaultValue: 1, min: 1, max: 20 },
            { key: 'num_options', label: 'Options', labelKey: 'numOptions', type: 'number', defaultValue: 4, min: 2, max: 8 },
            { key: 'num_correct_min', label: 'Correct min', labelKey: 'correctMin', type: 'number', defaultValue: 2, min: 2, max: 7 },
            { key: 'num_correct_max', label: 'Correct max', labelKey: 'correctMax', type: 'number', defaultValue: 2, min: 2, max: 7 },
        ],
    },
    {
        slug: 'true_false',
        label: getQuestionTypeLabel('true_false', 'en'),
        code: 'true_false',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_questions', label: 'Sub-questions', labelKey: 'numQuestions', type: 'number', defaultValue: 1, min: 1, max: 20 },
        ],
    },
    {
        slug: 'gap_typing',
        label: getQuestionTypeLabel('gap_typing', 'en'),
        code: 'gap_typing',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_blanks', label: 'Blanks', labelKey: 'numBlanks', type: 'number', defaultValue: 1, min: 1, max: 15 },
        ],
    },
    {
        slug: 'gap_dropdown',
        label: getQuestionTypeLabel('gap_dropdown', 'en'),
        code: 'gap_dropdown',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_blanks', label: 'Blanks', labelKey: 'numBlanks', type: 'number', defaultValue: 1, min: 1, max: 15 },
            { key: 'num_choices', label: 'Choices', labelKey: 'numChoices', type: 'number', defaultValue: 4, min: 2, max: 8 },
        ],
    },
    {
        slug: 'gap_word_bank',
        label: getQuestionTypeLabel('gap_word_bank', 'en'),
        code: 'gap_word_bank',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_blanks', label: 'Blanks', labelKey: 'numBlanks', type: 'number', defaultValue: 1, min: 1, max: 15 },
            { key: 'word_bank_size', label: 'Bank size', labelKey: 'wordBankSize', type: 'number', defaultValue: 8, min: 3, max: 20 },
        ],
    },
    {
        slug: 'gap_drag_words',
        label: getQuestionTypeLabel('gap_drag_words', 'en'),
        code: 'gap_drag_words',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_blanks', label: 'Blanks', labelKey: 'numBlanks', type: 'number', defaultValue: 1, min: 1, max: 15 },
            {
                key: 'distractors_per_blank',
                label: 'Distractors per blank', labelKey: 'distractorsPerBlank',
                type: 'number',
                defaultValue: 2,
                min: 1,
                max: 5,
            },
        ],
    },
    {
        slug: 'matching_text_pairs',
        label: getQuestionTypeLabel('matching_text_pairs', 'en'),
        code: 'matching_text_pairs',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_left', label: 'Left items', labelKey: 'leftItems', type: 'number', defaultValue: 4, min: 2, max: 10 },
            { key: 'num_right', label: 'Right items', labelKey: 'rightItems', type: 'number', defaultValue: 6, min: 2, max: 12 },
        ],
    },
    {
        slug: 'matching_zone_drop',
        label: getQuestionTypeLabel('matching_zone_drop', 'en'),
        code: 'matching_zone_drop',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_left', label: 'Zones', labelKey: 'numZones', type: 'number', defaultValue: 4, min: 2, max: 10 },
            { key: 'num_right', label: 'Items', labelKey: 'numItems', type: 'number', defaultValue: 6, min: 2, max: 12 },
        ],
    },
    {
        slug: 'ordering',
        label: getQuestionTypeLabel('ordering', 'en'),
        code: 'ordering',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'num_items', label: 'Items', labelKey: 'numItems', type: 'number', defaultValue: 5, min: 3, max: 8 },
        ],
    },
    {
        slug: 'speak',
        label: getQuestionTypeLabel('speak', 'en'),
        code: 'speak',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'max_words', label: 'Max words', labelKey: 'maxWords', type: 'number', defaultValue: 50, min: 10, max: 200 },
        ],
    },
    {
        slug: 'write',
        label: getQuestionTypeLabel('write', 'en'),
        code: 'write',
        optionFields: [
            INTERACTION_MEDIA_FIELD,
            { key: 'word_count_min', label: 'Word min', labelKey: 'minWords', type: 'number', defaultValue: 150, min: 50, max: 500 },
            { key: 'word_count_max', label: 'Word max', labelKey: 'maxWords', type: 'number', defaultValue: 250, min: 100, max: 1000 },
        ],
    },
];

export function getInteractionCanonicalSlugs(): InteractionEnumSlug[] {
    return INTERACTION_CATALOG.map((e) => e.slug);
}

export function getInteractionDisplayInfo(locale?: string): Array<{
    slug: string;
    name: string;
    category?: string;
}> {
    return INTERACTION_CATALOG.map((e) => ({
        slug: e.slug,
        name: getQuestionTypeLabel(e.slug, locale),
        category: e.code,
    }));
}

export function getInteractionEntry(slug: string): InteractionCatalogEntry | undefined {
    return INTERACTION_CATALOG.find((e) => e.slug === slug);
}
